// src/features/vehicles/DeviceDrawerTerminalTab.jsx
import { useMemo, useState } from 'react';
import { useTerminalHistory } from '@/features/terminal/useTerminalHistory';
import { useTerminalStream } from '@/features/terminal/useTerminalStream';
import TerminalRow from '@/features/terminal/TerminalRow';
import { Pause, Play, ChevronDown } from 'lucide-react';

const TYPE_OPTIONS = [
  { key: 'event',    label: 'Events' },
  { key: 'position', label: 'Positions' },
  { key: 'command',  label: 'Commands' },
];

export default function DeviceDrawerTerminalTab({ deviceId }) {
  const [types, setTypes] = useState(['event', 'command']);
  const [paused, setPaused] = useState(false);
  const [windowStart, setWindowStart] = useState(() => new Date(Date.now() - 60 * 60 * 1000).toISOString());

  const { rows: history, loading, error, loadEarlier } = useTerminalHistory({
    deviceIds: [deviceId], types, from: windowStart, live: false, limit: 500,
  });

  const filter = useMemo(() => ({ deviceIds: [deviceId], types }), [deviceId, types]);
  const { rows: liveRows, status } = useTerminalStream(filter, { bufferMax: 500, paused });

  const merged = useMemo(() => {
    const seen = new Set();
    const all = [...liveRows, ...history];
    return all.filter(r => (seen.has(r.id) ? false : (seen.add(r.id), true)));
  }, [liveRows, history]);

  const toggleType = (k) => setTypes(ts => ts.includes(k) ? ts.filter(x => x !== k) : [...ts, k]);

  const handleLoadEarlier = () => {
    const newStart = new Date(new Date(windowStart).getTime() - 60 * 60 * 1000).toISOString();
    setWindowStart(newStart);
    loadEarlier(newStart, new Date().toISOString());
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 text-xs">
        <span className="text-slate-500">Show:</span>
        {TYPE_OPTIONS.map(o => (
          <label key={o.key} className="flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={types.includes(o.key)} onChange={() => toggleType(o.key)} />
            {o.label}
          </label>
        ))}
        <div className="ms-auto flex items-center gap-2">
          <span className={`text-[10px] ${status === 'open' ? 'text-emerald-600' : 'text-slate-400'}`}>
            {status === 'open' ? '● live' : status}
          </span>
          <button type="button" onClick={() => setPaused(p => !p)} className="flex items-center gap-1 px-2 py-1 rounded bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700">
            {paused ? <Play size={12}/> : <Pause size={12}/>}
            {paused ? 'Resume' : 'Pause'}
          </button>
        </div>
      </div>

      {error && <div className="text-xs text-rose-600">Error: {error}</div>}
      {loading && merged.length === 0 && <div className="text-xs text-slate-500">Loading…</div>}
      {!loading && merged.length === 0 && <div className="text-xs text-slate-500">No activity in the selected window.</div>}

      <ul className="max-h-[60vh] overflow-y-auto rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        {merged.map(r => <TerminalRow key={r.id} row={r} showDevice={false} />)}
      </ul>

      <button type="button" onClick={handleLoadEarlier} className="w-full flex items-center justify-center gap-1 py-2 text-xs rounded bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700">
        <ChevronDown size={12}/> Load earlier 1h
      </button>
    </div>
  );
}
